import { Button } from 'primereact/button';
import {
  CartItemInterface,
  useRemoveItemFromCartMutation,
  useUpdateCartItemsMutation,
} from 'graphql/generated/magentoApi';
import { InputNumber } from 'primereact/inputnumber';
import React, { ReactElement, useEffect, useState } from 'react';
import { useCartContext } from 'CartContext';
import { Link } from 'react-router-dom';
import './RightSidebarCart.scss';
import FocusLock from 'react-focus-lock';
import useDebounce from 'hooks/useDebounce';

type TypeItem = {
  __typename?: string;
};

interface RightSidebarCartProps {
  setVisibleRight: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function RightSidebarCart({
  setVisibleRight,
}: RightSidebarCartProps): ReactElement {
  const { cartId, items, taxes, subTotal, total, loading, updateItems } =
    useCartContext();
  const [{ fetching, data }, removeItem] = useRemoveItemFromCartMutation();
  const [, updateCartItems] = useUpdateCartItemsMutation();
  const [cartContextLoading, setCartContextLoading] =
    useState<boolean>(loading);

  const [idToRemove, setIdToRemove] = useState<string | undefined>();
  const [hasToUpdate, setHasToUpdate] = useState(false);

  useEffect(() => {
    if (hasToUpdate && !fetching && data) {
      setTimeout(() => {
        updateItems(
          (data.removeItemFromCart?.cart
            .items as unknown as CartItemInterface[]) || [],
        );

        setHasToUpdate(false);
        setCartContextLoading(true);
      }, 250);
    }
  }, [hasToUpdate, fetching, data, cartContextLoading, updateItems]);

  const onClickXBtn = (uid: string) => {
    if (!fetching && !hasToUpdate) {
      setIdToRemove(uid);
      removeItem({
        input: {
          cart_id: cartId,
          cart_item_uid: uid,
        },
      });

      setHasToUpdate(true);
    }
  };

  const syncCart = () => {
    updateCartItems({
      input: {
        cart_id: cartId,
        cart_items: items.map(item => ({
          cart_item_uid: item.uid,
          quantity: item.quantity,
        })),
      },
    });
  };

  const resetDebounce = useDebounce(syncCart, 3000);

  const onClickQtyBtn = (uid: string, quantity: number) => {
    const newItems = items.map(item =>
      item.uid === uid ? { ...item, quantity } : item,
    );
    updateItems(newItems);
    resetDebounce();
  };

  const cartItem = (item: CartItemInterface & TypeItem) => {
    const subscriptionText =
      item.product.is_subscription === 1
        ? `${item.product.name}\nMonthly Subscription`
        : `${item.product.name}`;
    const subscriptionPricing =
      item.product.is_subscription === 1
        ? `$${(
            item.quantity *
            Number(item.product.price_range.maximum_price.final_price.value)
          ).toFixed(2)}/mo`
        : `$${(
            item.quantity *
            Number(item.product.price_range.maximum_price.final_price.value)
          ).toFixed(2)}`;
    return (
      <div
        className="summary__cart__item grid border-none border-bottom-1 border-300 py-3"
        key={item.uid}
      >
        <Link
          to={`/product/${item.product.sku}`}
          className="flex col-4 justify-content-center"
        >
          <picture className="summary__cart__item__image ">
            <img
              className="w-20"
              alt={String(item.product.name) || ''}
              src={String(item.product.image?.url) || ''}
            />
          </picture>
        </Link>

        <div className="flex flex-row w-8">
          <div className="flex flex-column">
            <span className="product-title">{`${subscriptionText}`}</span>
            <span className="product-price">{subscriptionPricing}</span>
          </div>

          <div className="x-btn__container flex align-items-center ml-auto">
            <Button
              onClick={() => onClickXBtn(item.uid)}
              className="x-btn flex w-full sm:w-auto flex cursor-pointer ml-auto"
              loading={item.uid === idToRemove && fetching}
            >
              {item.uid !== idToRemove && <i className="pi pi-times" />}
            </Button>
          </div>
        </div>
        <div className="flex flex-row w-2">
          <InputNumber
            className="c-button-gray my-2 w-4 ml-auto mr-1"
            value={item.quantity}
            buttonLayout="horizontal"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            showButtons
            min={1}
            max={99}
            disabled={
              !item.product.name.toLowerCase().includes('donation') &&
              item.__typename === 'VirtualCartItem'
            }
            onValueChange={e => onClickQtyBtn(item.uid, e.value)}
          />
        </div>
      </div>
    );
  };

  return (
    <FocusLock>
      <div className="cart-summary-sidebar">
        <div className="flex justify-content-end">
          <Button
            onClick={() => setVisibleRight(false)}
            className="c-button-gray cursor-pointer px-2 py-1"
            aria-label="Close summary card"
          >
            Close
          </Button>
        </div>
        <h2 className="title">Cart Summary</h2>
        <div className="cart-summary-card">
          {!items.length ? (
            <div
              className="flex w-full align-items-center justify-content-center"
              style={{ minHeight: '200px' }}
            >
              <p className="font-bold text-900">Empty Cart</p>
            </div>
          ) : (
            <div className="p-2">
              <div className="cart-summary__list">
                {items.map(item => cartItem(item))}
              </div>
              <div className="border-none border-bottom-1 border-300 mb-2">
                <p className="flex justify-content-between text-700  mb-2 text-xs">
                  Subtotal
                  <span>{`$${subTotal}`}</span>
                </p>
                <p className="flex justify-content-between text-700 mt-0 mb-2 text-xs">
                  Taxes
                  <span>
                    {taxes !== '0.00' ? `$${taxes}` : 'Calculated at checkout'}
                  </span>
                </p>
              </div>

              <p className="flex justify-content-between text-700 font-bold font-medium my-4">
                Total
                <span className="text-700">
                  <span className="mr-2 text-xs font-medium">USD</span>${total}
                </span>
              </p>
              <div className="flex flex-column w-full p-0">
                <Link to="/cart" className="shop-button black solid mx-0 mb-2">
                  View Cart
                </Link>
                <Link to="/checkout" className="shop-button green solid mx-0">
                  Checkout
                </Link>
                <Button
                  className="p-sidebar-close p-link mt-3 text-center text-underline"
                  type="button"
                  label="Continue Shopping"
                  aria-label="close"
                  onClick={() => setVisibleRight(false)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </FocusLock>
  );
}
